import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contrast, isHex } from '@/lib/contrast';

interface Props {
  label: string;
  value: string;
  onChange: (hex: string) => void;
  /** check WCAG contrast against the page background (skip for surface colours) */
  checkContrast?: boolean;
  /** colour the label with the value itself */
  tint?: boolean;
}

const BG = '#0a0c0d';

export function ColorField({ label, value, onChange, checkContrast = true, tint = true }: Props) {
  const { t } = useTranslation();
  const id = useId();
  const [text, setText] = useState(value.toUpperCase());
  // reset the draft when the committed value changes from outside (preset, reset, import)
  const [prev, setPrev] = useState(value);
  if (prev !== value) {
    setPrev(value);
    setText(value.toUpperCase());
  }

  const commit = (): void => {
    const v = text.trim().replace(/^#?/, '#').toLowerCase();
    if (isHex(v)) onChange(v);
    else setText(value.toUpperCase());
  };
  const ratio = checkContrast ? contrast(value, BG) : null;
  const low = ratio !== null && ratio < 4.5;

  return (
    <div className="flex flex-col gap-2 rounded-[14px] border border-line bg-surface-2 p-3.5">
      <label htmlFor={`${id}-hex`} className="label" style={tint ? { color: value } : undefined}>
        {label}
      </label>
      <div className="flex items-center gap-2.5">
        <span className="relative size-9 flex-none overflow-hidden rounded-[10px] border-2 border-line-strong" style={{ background: value }}>
          <input
            id={`${id}-pick`}
            type="color"
            aria-label={label}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="absolute -inset-2 size-[200%] cursor-pointer opacity-0"
          />
        </span>
        <input
          id={`${id}-hex`}
          type="text"
          maxLength={7}
          spellCheck={false}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === 'Enter' && commit()}
          className="num h-9 min-w-0 flex-1 rounded-lg border border-line-strong bg-bg px-2.5 font-bold tracking-[0.06em] uppercase"
        />
      </div>
      {ratio !== null && (
        <span className={`num text-[11px] font-bold tracking-[0.08em] ${low ? 'text-err' : 'text-fg-muted'}`}>
          {ratio.toFixed(1)}:1{low && ` · ${t('settings.lowContrast')}`}
        </span>
      )}
    </div>
  );
}
